// /app/frontend/src/components/DispatchFollowupQueue.jsx
// Sprint B-3 — Followup queue visible in Dispatch + Admin approval flow
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { AlertCircle, CheckCircle, Eye, Image as ImageIcon, ThumbsUp, ThumbsDown } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_COLORS = {
  sent: "bg-blue-100 text-blue-700",
  acknowledged: "bg-purple-100 text-purple-700",
  in_progress: "bg-amber-100 text-amber-800",
  pending_review: "bg-orange-100 text-orange-800",
  needs_rework: "bg-red-100 text-red-700",
};

export default function DispatchFollowupQueue() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewing, setReviewing] = useState(null);
  const [reviewReason, setReviewReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchQueue = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/dispatch/followup-queue`);
      setItems(res.data.items || []);
    } catch (e) {
      console.error("followup-queue fetch failed", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQueue();
    const t = setInterval(fetchQueue, 30_000); // refresh every 30s
    return () => clearInterval(t);
  }, [fetchQueue]);

  const review = async (decision) => {
    if (!reviewing) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/finance/followups/${reviewing.id}/approve`, {
        decision,
        reason: reviewReason,
      });
      setReviewing(null);
      setReviewReason("");
      await fetchQueue();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="text-xs text-gray-400 italic py-2">Loading...</div>;

  const pendingReview = items.filter(i => i.awaiting_review);
  const active = items.filter(i => !i.awaiting_review);

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-testid="dispatch-followup-queue">
      <div className="px-4 py-3 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-orange-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AlertCircle className="h-4 w-4 text-orange-600" />
          <span className="font-bold text-sm text-orange-900">Follow-up Queue</span>
          <span className="text-xs text-orange-700">({items.length} ativos)</span>
        </div>
        {pendingReview.length > 0 && (
          <span data-testid="followup-queue-pending-review-count" className="text-xs font-bold bg-orange-500 text-white px-2 py-0.5 rounded">
            {pendingReview.length} aguardando revisão
          </span>
        )}
      </div>

      {items.length === 0 ? (
        <div className="px-4 py-6 text-center text-xs text-gray-400 italic">Nenhum follow-up ativo</div>
      ) : (
        <div className="divide-y divide-gray-100">
          {[...pendingReview, ...active].map(it => (
            <div key={it.id} data-testid={`followup-row-${it.id}`} className="px-4 py-3 hover:bg-gray-50/50">
              <div className="flex items-center gap-3">
                <span className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded ${STATUS_COLORS[it.status] || "bg-gray-100 text-gray-600"}`}>
                  {it.status === "pending_review" ? "Aguarda revisão" : it.status}
                </span>
                <span className="font-mono text-xs font-semibold text-[#1F2937]">{it.order_number}</span>
                <span className="text-[11px] text-gray-500">{it.inspector || it.assigned_to}</span>
                <div className="flex items-center gap-1 ml-auto">
                  <span className={`text-[10px] flex items-center gap-0.5 ${it.has_error_photo ? "text-green-600" : "text-gray-300"}`}>
                    <ImageIcon className="h-3 w-3" />1 erro
                  </span>
                  <span className={`text-[10px] flex items-center gap-0.5 ${it.has_correct_photo ? "text-green-600" : "text-gray-300"}`}>
                    <ImageIcon className="h-3 w-3" />2 correto
                  </span>
                  {it.awaiting_review && (
                    <button
                      onClick={() => setReviewing(it)}
                      className="ml-2 text-[11px] font-bold text-white bg-[#2F5D8A] hover:bg-[#24496e] px-2 py-1 rounded"
                      data-testid={`followup-review-btn-${it.id}`}
                    >
                      <Eye className="h-3 w-3 inline mr-0.5" /> Revisar
                    </button>
                  )}
                </div>
              </div>
              {it.subject && <p className="text-[11px] text-gray-600 mt-1 ml-1 leading-relaxed">{it.subject}</p>}
            </div>
          ))}
        </div>
      )}

      {reviewing && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[1100] p-4" data-testid="followup-review-modal">
          <div className="bg-white rounded-xl w-full max-w-2xl shadow-2xl flex flex-col max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-gray-100 flex items-start justify-between">
              <div>
                <h2 className="text-base font-bold text-[#1F2937]">Revisar Follow-up</h2>
                <p className="text-[10px] text-gray-500 mt-0.5">Order {reviewing.order_number} · {reviewing.inspector || reviewing.assigned_to}</p>
              </div>
              <button onClick={() => setReviewing(null)} className="text-gray-400 hover:text-gray-700 text-xl leading-none" data-testid="followup-review-close">×</button>
            </div>
            <div className="p-5 space-y-4">
              {reviewing.subject && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
                  <p className="font-semibold mb-1">{reviewing.subject}</p>
                  {reviewing.message && <p className="leading-relaxed">{reviewing.message}</p>}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-bold text-red-700 mb-1.5">1. Foto do ERRO</p>
                  {reviewing.error_photo_url ? (
                    <img src={`${BACKEND_URL}${reviewing.error_photo_url}`} alt="error" className="w-full h-48 object-cover rounded border" data-testid="followup-review-photo-error" />
                  ) : (
                    <div className="h-48 rounded border bg-gray-100 flex items-center justify-center text-xs text-gray-400">No photo</div>
                  )}
                </div>
                <div>
                  <p className="text-xs font-bold text-green-700 mb-1.5">2. Foto do CORRETO</p>
                  {reviewing.correct_photo_url ? (
                    <img src={`${BACKEND_URL}${reviewing.correct_photo_url}`} alt="correct" className="w-full h-48 object-cover rounded border" data-testid="followup-review-photo-correct" />
                  ) : (
                    <div className="h-48 rounded border bg-gray-100 flex items-center justify-center text-xs text-gray-400">No photo</div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Comentário (opcional)</label>
                <textarea
                  value={reviewReason}
                  onChange={e => setReviewReason(e.target.value)}
                  rows={3}
                  placeholder="Motivo de aprovação ou rejeição..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-[#2F5D8A] focus:outline-none resize-none"
                  data-testid="followup-review-reason"
                />
              </div>

              <div className="flex gap-2 pt-1">
                <button onClick={() => review("reject")} disabled={submitting} className="flex-1 py-2 bg-red-100 hover:bg-red-200 text-red-700 text-sm font-bold rounded-lg disabled:opacity-50 flex items-center justify-center gap-1.5" data-testid="followup-review-reject">
                  <ThumbsDown className="h-4 w-4" /> Rejeitar (refazer)
                </button>
                <button onClick={() => review("approve")} disabled={submitting} className="flex-1 py-2 bg-[#6B8F62] hover:bg-[#5a7a52] text-white text-sm font-bold rounded-lg disabled:opacity-50 flex items-center justify-center gap-1.5" data-testid="followup-review-approve">
                  <ThumbsUp className="h-4 w-4" /> Aprovar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
